import React, { memo, useState } from 'react';
import { Box, Typography, LinearProgress, List, ListItem, ListItemText, Collapse, IconButton } from '@mui/material';
import { isValid } from 'date-fns';
import { styled } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const StatisticsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(3),
  backgroundColor: '#f8f9fa',
  borderRadius: theme.shape.borderRadius,
  border: '1px solid #e9ecef'
}));

const StatisticItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1),
  '&:not(:last-child)': {
    borderBottom: '1px solid #e9ecef'
  }
}));

// Nuevo componente para grupos de estadísticas
const StatisticGroup = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: theme.shape.borderRadius,
  border: '1px solid #e9ecef',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  '&:last-child': {
    marginBottom: 0
  }
}));

const GroupTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#495057',
  marginBottom: theme.spacing(1.5),
  fontSize: '0.75rem',
  letterSpacing: '0.8px',
  textTransform: 'uppercase',
  borderBottom: '2px solid #e9ecef',
  paddingBottom: theme.spacing(0.5)
}));

const StatisticRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0.75, 0),
  '&:not(:last-child)': {
    borderBottom: '1px solid #f1f3f4'
  }
}));

const StatisticLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#495057',
  fontWeight: 500
}));

const StatisticValue = styled(Typography)(({ theme, color }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  minWidth: '40px',
  textAlign: 'right'
}));

const StatusIndicator = styled(Box)(({ theme, color }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: color || theme.palette.grey[400],
  marginRight: theme.spacing(1),
  flexShrink: 0
}));

// Estilos estáticos memoizados
const staticStyles = {
  container: { width: '100%' },
  header: { display: 'flex', alignItems: 'center', mb: 1 },
  batchName: { color: '#666', mr: 2 },
  timeText: { color: '#666' },
  progressContainer: { position: 'relative' },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(27, 40, 94, 0.1)',
  },
  statsGrid: { 
    mt: 2,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 2
  }
};

// Pre-compute static styles
const ERROR_BOX_STYLE = {
  ...staticStyles.statBox?.base,
  backgroundColor: 'rgba(244, 67, 54, 0.05)',
  border: '1px solid rgba(244, 67, 54, 0.1)', 
  p: 2,
  borderRadius: 1,
};

const NORMAL_BOX_STYLE = {
  ...staticStyles.statBox?.base,
  backgroundColor: 'rgba(27, 40, 94, 0.05)',
  border: '1px solid rgba(27, 40, 94, 0.1)',
  p: 2,
  borderRadius: 1,
};

const WARNING_BOX_STYLE = {
  ...staticStyles.statBox?.base,
  backgroundColor: 'rgba(237, 108, 2, 0.05)',
  border: '1px solid rgba(237, 108, 2, 0.1)',
  p: 2,
  borderRadius: 1,
};

const ERROR_LABEL_STYLE = {
  color: '#d32f2f',
  fontFamily: '"Trasandina Web", Trasandina, sans-serif',
  fontSize: '0.75rem',
  mb: 0.5
};

const WARNING_LABEL_STYLE = {
  color: '#ed6c02',
  fontFamily: '"Trasandina Web", Trasandina, sans-serif',
  fontSize: '0.75rem',
  mb: 0.5
};

const NORMAL_LABEL_STYLE = {
  color: '#666',
  fontFamily: '"Trasandina Web", Trasandina, sans-serif',
  fontSize: '0.75rem',
  mb: 0.5
};

const ERROR_VALUE_STYLE = {
  color: '#d32f2f',
  fontFamily: '"Trasandina Web", Trasandina, sans-serif',
  fontWeight: 600
};

const WARNING_VALUE_STYLE = {
  color: '#ed6c02',
  fontFamily: '"Trasandina Web", Trasandina, sans-serif',
  fontWeight: 600
};

const NORMAL_VALUE_STYLE = {
  color: '#1B285E',
  fontFamily: '"Trasandina Web", Trasandina, sans-serif',
  fontWeight: 600
};

const ACTIVE_PROGRESS_STYLE = {
  ...staticStyles.progressBar,
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#1B285E',
    borderRadius: 4,
  }
};

const INACTIVE_PROGRESS_STYLE = {
  ...staticStyles.progressBar,
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#FDBD10',
    borderRadius: 4,
  }
};

const StatBox = memo(({ label, value, error, warning }) => {
  const getBoxStyle = () => {
    if (error) return ERROR_BOX_STYLE;
    if (warning) return WARNING_BOX_STYLE;
    return NORMAL_BOX_STYLE;
  };

  const getLabelStyle = () => {
    if (error) return ERROR_LABEL_STYLE;
    if (warning) return WARNING_LABEL_STYLE;
    return NORMAL_LABEL_STYLE;
  };

  const getValueStyle = () => {
    if (error) return ERROR_VALUE_STYLE;
    if (warning) return WARNING_VALUE_STYLE;
    return NORMAL_VALUE_STYLE;
  };

  return (
    <Box sx={getBoxStyle()}>
      <Typography variant="body2" sx={getLabelStyle()}>
        {label}
      </Typography>
      <Typography variant="h6" sx={getValueStyle()}>
        {value}
      </Typography>
    </Box>
  );
});

const ProgressBar = memo(({ progress, isActive }) => {
  const getProgressStyle = () => {
    if (!isActive && progress === 100) {
      return {
        ...staticStyles.progressBar,
        '& .MuiLinearProgress-bar': {
          backgroundColor: '#4CAF50', // Verde para completado
          borderRadius: 4,
        }
      };
    } else if (isActive) {
      return {
        ...staticStyles.progressBar,
        '& .MuiLinearProgress-bar': {
          backgroundColor: '#FDBD10', // Amarillo para en progreso
          borderRadius: 4,
        }
      };
    } else {
      return {
        ...staticStyles.progressBar,
        '& .MuiLinearProgress-bar': {
          backgroundColor: '#1B285E', // Azul por defecto
          borderRadius: 4,
        }
      };
    }
  };

  return (
    <LinearProgress 
      variant="determinate" 
      value={progress} 
      sx={getProgressStyle()}
    />
  );
}, (prevProps, nextProps) => {
  return prevProps.progress === nextProps.progress && 
         prevProps.isActive === nextProps.isActive;
});

const TimeDisplay = memo(({ startTime }) => {
  if (!startTime) return null;

  try {
    const date = new Date(startTime);
    if (!isValid(date)) return null;
    
    const formattedTime = date.toLocaleString('es', { 
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    return (
      <Typography variant="body2" sx={staticStyles.timeText}>
        Ultimo proceso iniciado el: {formattedTime}
      </Typography>
    );
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
}, (prevProps, nextProps) => prevProps.startTime === nextProps.startTime);

const BPSStats = memo(({ statistics }) => (
  <Box sx={staticStyles.statsGrid}>
    <StatBox key="total" label="Total Procesados" value={statistics?.totalProspectos ?? 0} />
    <StatBox key="noAfiliables" label="No Afiliables" value={statistics?.noAfiliables ?? 0} />
    <StatBox key="afiliables" label="Afiliables" value={statistics?.afiliables ?? 0} />
    <StatBox key="personasInexistentes" label="Personas Inexistentes" value={statistics?.personasInexistentes ?? 0} warning />
    <StatBox key="errores" label="Errores" value={statistics?.errores ?? 0} error />
  </Box>
), (prevProps, nextProps) => {
  if (!prevProps.statistics && !nextProps.statistics) return true;
  if (!prevProps.statistics || !nextProps.statistics) return false;
  return JSON.stringify(prevProps.statistics) === JSON.stringify(nextProps.statistics);
});


const StatisticsComponent = memo(({ statistics, processType }) => {
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [inexistentesExpanded, setInexistentesExpanded] = useState(false);
  
  console.log('StatisticsComponent received:', { statistics, processType });
  
  if (!statistics) {
    console.log('Statistics is null, returning null');
    return null;
  }

  // Separar errores de personas inexistentes
  const erroresReales = statistics?.erroresDetallados?.filter(error => 
    !error.Mensaje?.includes('Persona inexistente')
  ) || [];
  
  const personasInexistentes = statistics?.erroresDetallados?.filter(error => 
    error.Mensaje?.includes('Persona inexistente')
  ) || [];

  // Si es Apollo, mantener la lógica original
  if (processType === 'Apollo') {
    return (
      <StatisticsContainer>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Estadísticas Apollo
        </Typography>
        <List>
          {Object.entries(statistics).map(([key, value]) => {
            console.log('Rendering statistic:', { key, value });
            let label = key;
            let color = 'text.primary';
            let displayValue = value;
            
            // Mapear las claves a nombres más amigables
            switch(key) {
              case 'erroresDetallados':
                return null;
              case 'procesadosCount':
                label = 'Procesados sin ningun error';
                break;
              case 'totalProspectos':
                label = 'Total de registros a procesar en este batch';
                break;
              case 'emailsEncontrados':
                label = 'Emails encontrados';
                break;
              case 'personasEncontradas':
                label = 'Personas encontradas';
                break;
              case 'errores':
                label = 'Registros con errores parciales';
                color = value > 0 ? 'error.main' : 'text.primary';
                break;
              case 'afiliables':
                label = 'Afiliables';
                color = 'success.main';
                break;
              case 'noAfiliables':
                label = 'No Afiliables';
                break;
              case 'personasInexistentes':
                label = 'Personas Inexistentes';
                color = 'warning.main';
                break;
              case 'obtainBirthDates':
                label = 'Obtener fechas de nacimiento para todos los prospectos afiliables';
                displayValue = value ? 'Sí' : 'No';
                break;
              case 'birthDatesObtained':
                label = 'Fechas de nacimiento obtenidas';
                color = 'success.main';
                if (!statistics.obtainBirthDates) return null;
                break;
              default:
                break;
            }

            return (
              <ListItem key={key}>
                <ListItemText
                  primary={
                    <Typography variant="body2" color={color}>
                      {label}: {displayValue}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}

          {/* Mostrar personas inexistentes en una sección separada */}
          {personasInexistentes && personasInexistentes.length > 0 && (
            <>
              <ListItem button onClick={() => setInexistentesExpanded(!inexistentesExpanded)}>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="warning.main">
                      Personas Inexistentes ({personasInexistentes.length}):
                    </Typography>
                  }
                />
                <IconButton edge="end" aria-label="expand">
                  {inexistentesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </ListItem>
              <Collapse in={inexistentesExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {personasInexistentes.map((error, index) => (
                    <ListItem key={index} sx={{ pl: 4 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="warning.main">
                            {`${error.ProspectoId ? `ID: ${error.ProspectoId}` : ''} ${error.Cedula ? `Cédula: ${error.Cedula}` : ''} ${error.Mensaje || ''} ${error.Fecha ? `(${error.Fecha})` : ''}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {/* Mostrar errores reales en una sección separada */}
          {erroresReales && erroresReales.length > 0 && (
            <>
              <ListItem button onClick={() => setErrorsExpanded(!errorsExpanded)}>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="error">
                      Errores Detallados ({erroresReales.length}):
                    </Typography>
                  }
                />
                <IconButton edge="end" aria-label="expand">
                  {errorsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </ListItem>
              <Collapse in={errorsExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {erroresReales.map((error, index) => (
                    <ListItem key={index} sx={{ pl: 4 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="error">
                            {`${error.ProspectoId ? `ID: ${error.ProspectoId}` : ''} ${error.Cedula ? `Cédula: ${error.Cedula}` : ''} ${error.Mensaje || ''} ${error.Fecha ? `(${error.Fecha})` : ''}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </List>
      </StatisticsContainer>
    );
  }

  // Nueva lógica agrupada para BPS
  const statisticsGroups = [
    {
      title: "PROCESAMIENTO",
      items: [
        { key: 'totalProspectos', label: 'Total de registros', value: statistics.totalProspectos },
        { key: 'procesadosCount', label: 'Procesados exitosamente', value: statistics.procesadosCount }
      ]
    },
    {
      title: "RESULTADOS", 
      items: [
        { key: 'afiliables', label: 'Afiliables', value: statistics.afiliables, color: '#4caf50' },
        { key: 'noAfiliables', label: 'No Afiliables', value: statistics.noAfiliables }
      ]
    },
    {
      title: "PROBLEMAS",
      items: [
        { key: 'personasInexistentes', label: 'Personas Inexistentes', value: statistics.personasInexistentes, color: '#ff9800' },
        { key: 'errores', label: 'Errores técnicos', value: statistics.errores, color: '#f44336' }
      ]
    }
  ];

  // Solo agregar grupo de fechas de nacimiento si está habilitado
  if (statistics.obtainBirthDates) {
    statisticsGroups.push({
      title: "FECHAS DE NACIMIENTO",
      items: [
        { key: 'birthDatesObtained', label: 'Obtenidas exitosamente', value: statistics.birthDatesObtained || 0, color: '#4caf50' }
      ]
    });
  }

  // Función para obtener el color del indicador
  const getIndicatorColor = (item) => {
    if (item.color) return item.color;
    if (item.value > 0) return '#6c757d';
    return '#dee2e6';
  };

  return (
    <StatisticsContainer>
      <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600, color: '#1B285E' }}>
        Estadísticas BPS
      </Typography>
      
      {statisticsGroups.map((group, groupIndex) => (
        <StatisticGroup key={group.title}>
          <GroupTitle variant="body2">
            {group.title}
          </GroupTitle>
          {group.items.map((item) => (
            <StatisticRow key={item.key}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StatusIndicator color={getIndicatorColor(item)} />
                <StatisticLabel>
                  {item.label}
                </StatisticLabel>
              </Box>
              <StatisticValue color={item.color}>
                {item.value ?? 0}
              </StatisticValue>
            </StatisticRow>
          ))}
        </StatisticGroup>
      ))}

      {/* Mostrar personas inexistentes en una sección separada */}
      {personasInexistentes && personasInexistentes.length > 0 && (
        <StatisticGroup>
          <Box 
            onClick={() => setInexistentesExpanded(!inexistentesExpanded)}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '&:hover': { backgroundColor: '#f8f9fa' },
              borderRadius: 1,
              p: 1,
              mx: -1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StatusIndicator color="#ff9800" />
              <StatisticLabel color="warning.main">
                Personas Inexistentes ({personasInexistentes.length})
              </StatisticLabel>
            </Box>
            <IconButton size="small" aria-label="expand">
              {inexistentesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={inexistentesExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, pl: 3 }}>
              {personasInexistentes.map((error, index) => (
                <Box key={index} sx={{ py: 0.5, fontSize: '0.8rem', color: '#6c757d' }}>
                  {`${error.ProspectoId ? `ID: ${error.ProspectoId}` : ''} ${error.Cedula ? `Cédula: ${error.Cedula}` : ''} ${error.Mensaje || ''} ${error.Fecha ? `(${error.Fecha})` : ''}`}
                </Box>
              ))}
            </Box>
          </Collapse>
        </StatisticGroup>
      )}

      {/* Mostrar errores reales en una sección separada */}
      {erroresReales && erroresReales.length > 0 && (
        <StatisticGroup>
          <Box 
            onClick={() => setErrorsExpanded(!errorsExpanded)}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '&:hover': { backgroundColor: '#f8f9fa' },
              borderRadius: 1,
              p: 1,
              mx: -1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StatusIndicator color="#f44336" />
              <StatisticLabel color="error">
                Errores Detallados ({erroresReales.length})
              </StatisticLabel>
            </Box>
            <IconButton size="small" aria-label="expand">
              {errorsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={errorsExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, pl: 3 }}>
              {erroresReales.map((error, index) => (
                <Box key={index} sx={{ py: 0.5, fontSize: '0.8rem', color: '#6c757d' }}>
                  {`${error.ProspectoId ? `ID: ${error.ProspectoId}` : ''} ${error.Cedula ? `Cédula: ${error.Cedula}` : ''} ${error.Mensaje || ''} ${error.Fecha ? `(${error.Fecha})` : ''}`}
                </Box>
              ))}
            </Box>
          </Collapse>
        </StatisticGroup>
      )}
    </StatisticsContainer>
  );
}, (prevProps, nextProps) => {
  console.log('StatisticsComponent memo comparison:', {
    prev: prevProps,
    next: nextProps,
    areEqual: JSON.stringify(prevProps.statistics) === JSON.stringify(nextProps.statistics)
  });
  
  if (!prevProps.statistics && !nextProps.statistics) return true;
  if (!prevProps.statistics || !nextProps.statistics) return false;
  return JSON.stringify(prevProps.statistics) === JSON.stringify(nextProps.statistics);
});

export const BatchProgress = memo(({ name, progress, isActive, startTime, batchName, statistics, obtainBirthDates }) => (
  <Box sx={staticStyles.container}>
    <Box sx={staticStyles.header}>
      {batchName && (
        <Typography variant="body2" sx={staticStyles.batchName}>
          {batchName}
        </Typography>
      )}
      <TimeDisplay startTime={startTime} />
    </Box>

    <Box sx={staticStyles.progressContainer}>
      <ProgressBar progress={progress} isActive={isActive} />
    </Box>

    {statistics && <StatisticsComponent statistics={statistics} processType={name} />}
  </Box>
), (prevProps, nextProps) => {
  if (prevProps.name !== nextProps.name) return false;
  if (prevProps.progress !== nextProps.progress) return false;
  if (prevProps.isActive !== nextProps.isActive) return false;
  if (prevProps.startTime !== nextProps.startTime) return false;
  if (prevProps.batchName !== nextProps.batchName) return false;
  if (prevProps.obtainBirthDates !== nextProps.obtainBirthDates) return false;
  if (!prevProps.statistics && !nextProps.statistics) return true;
  if (!prevProps.statistics || !nextProps.statistics) return false;
  return JSON.stringify(prevProps.statistics) === JSON.stringify(nextProps.statistics);
});